import { handTotal, type BlackjackState } from '../../engine/games/blackjack';
import type { Card } from '../../engine/cards';
import { money, withCommas } from '../../format';
import { cardsIn, PlayingCard } from '../parts';
import { Burst, Controls, secondChanceFrame, Stage, StatusLine, useAnimClock, useBurst, useLuckMoment, type TableProps } from './Frame';

/** Each dealer card turning over. */
const CARD_MS = 550;
/** How long the losing result holds before the second chance steps in. */
const LOST_MS = 1000;
/** The starburst on its own, before the new cards come. */
const BANNER_MS = 1300;
const END_HOLD_MS = 350;

type LuckKind = 'bust' | 'redeal' | 'blackjack';

const LUCK_COPY: Record<LuckKind, string> = {
  bust: 'That card would have bust you.',
  redeal: "The dealer's winning hand is re-dealt.",
  blackjack: 'Dealer blackjack: a new hole card.',
};

/** What the table looks like at one moment of the animation. */
interface View {
  dealer: Card[];
  /** Show a face-down card after the dealer's shown cards. */
  hole: boolean;
  /** Your hands, when they differ from the final ones (the bust card before it's swapped). */
  hands?: Card[][];
}

interface Plan {
  total: number;
  /** When the starburst appears (null: no second chance). */
  luckAt: number | null;
  /** When the second chance's outcome shows. */
  resolveAt: number;
  kind: LuckKind | null;
  at(t: number): View;
}

/**
 * The play's animation, built from the final state and the engine's
 * second-chance frame, which holds the losing picture: the dealer's winning
 * hand ("Dealer: 10♠ 9♥ (19)"), or your hand with the card that would have
 * bust you ("You: 7♦ 6♠ K♠ (23)").
 */
function plan(state: BlackjackState, frame: string | undefined): Plan {
  const final = state.dealer;
  const lines = frame?.split('\n') ?? [];
  const lostDealer = cardsIn(lines.find((l) => l.startsWith('Dealer:')) ?? '');
  const lostHands = lines.filter((l) => l.startsWith('You:')).map(cardsIn);
  const kind: LuckKind | null = !frame ? null : frame.includes('bust you') ? 'bust' : frame.includes('re-deals') ? 'redeal' : 'blackjack';
  const playing = state.phase === 'player';
  // The dealer turning over cards from the upcard, starting at `from` ms.
  const reveal = (cards: Card[], t: number, from: number): View => {
    const shown = Math.min(cards.length, 1 + Math.max(0, Math.floor((t - from) / CARD_MS)));
    // Until the second card turns over, it sits face down beside the upcard.
    return { dealer: cards.slice(0, shown), hole: shown < 2 };
  };
  const dealerMs = (cards: Card[]) => Math.max(0, cards.length - 1) * CARD_MS;

  if (kind === 'redeal' && lostDealer.length) {
    const luckAt = dealerMs(lostDealer) + LOST_MS;
    const resolveAt = luckAt + BANNER_MS;
    return {
      kind,
      luckAt,
      resolveAt: resolveAt + dealerMs(final),
      total: resolveAt + dealerMs(final) + END_HOLD_MS,
      at: (t) => (t < resolveAt ? reveal(lostDealer, t, 0) : reveal(final, t, resolveAt)),
    };
  }
  if (kind === 'blackjack' && lostDealer.length) {
    const luckAt = CARD_MS + LOST_MS;
    const resolveAt = luckAt + BANNER_MS;
    return {
      kind,
      luckAt,
      resolveAt,
      total: resolveAt + (playing ? 0 : dealerMs(final)) + END_HOLD_MS,
      at: (t) => {
        if (t < CARD_MS) return { dealer: lostDealer.slice(0, 1), hole: true };
        if (t < resolveAt) return { dealer: lostDealer, hole: false };
        return playing ? { dealer: final.slice(0, 1), hole: true } : reveal(final, t, resolveAt);
      },
    };
  }
  if (kind === 'bust' && lostHands.length) {
    const luckAt = LOST_MS;
    const resolveAt = luckAt + BANNER_MS;
    return {
      kind,
      luckAt,
      resolveAt,
      total: resolveAt + (playing ? 0 : dealerMs(final)) + END_HOLD_MS,
      at: (t) => {
        if (t < resolveAt) return { dealer: final.slice(0, 1), hole: true, hands: lostHands };
        return playing ? { dealer: final.slice(0, 1), hole: true } : reveal(final, t, resolveAt);
      },
    };
  }
  // No second chance: the dealer plays out card by card.
  return {
    kind: null,
    luckAt: null,
    resolveAt: 0,
    total: (playing ? 0 : dealerMs(final)) + END_HOLD_MS,
    at: (t) => (playing ? { dealer: final.slice(0, 1), hole: true } : reveal(final, t, 0)),
  };
}

export function BlackjackTable({ snapshot, actions }: TableProps) {
  const run = snapshot.run!;
  const state = run.tables.blackjack?.state as BlackjackState | undefined;
  const anim = snapshot.anim;
  const frame = secondChanceFrame(snapshot);
  const p = state ? plan(state, frame) : null;
  const elapsed = useAnimClock(snapshot, actions, anim && p ? p.total : 0);
  const [burst, fading, setBurst] = useBurst();
  useLuckMoment(snapshot, elapsed, p?.luckAt ?? null, () => ({ what: LUCK_COPY[p!.kind!], saved: !!state?.lucky }), setBurst);

  if (!state || state.phase === 'idle') {
    return (
      <>
        <section className="dealer-band"><span className="dealer-label">Dealer</span></section>
        <Stage><p className="felt-hint">Place your bet and deal.</p></Stage>
        <Controls snapshot={snapshot} actions={actions} onAct={() => setBurst(null)} />
      </>
    );
  }

  const view: View = anim && p ? p.at(elapsed) : state.phase === 'player' ? { dealer: state.dealer.slice(0, 1), hole: true } : { dealer: state.dealer, hole: false };
  const hands = view.hands ?? state.hands.map((h) => h.cards);
  const dealerTotal = handTotal(view.dealer).total;
  const finished = state.phase === 'done' && !anim;
  const ringSwapped = !!burst?.saved && p?.kind === 'bust' && !view.hands;

  return (
    <>
      <section className="dealer-band">
        <div className="cards">
          {view.dealer.map((c, i) => <PlayingCard key={i} card={c} />)}
          {view.hole && <PlayingCard hidden />}
        </div>
        <div className="dealer-label">
          Dealer
          <span className="dealer-sub">{view.hole ? `shows ${dealerTotal}` : dealerTotal > 21 ? `bust (${dealerTotal})` : `has ${dealerTotal}`}</span>
        </div>
      </section>
      <Stage onClick={anim ? actions.skip : undefined}>
        <Burst burst={burst} fading={fading} pending={!!anim && !!p && elapsed < p.resolveAt} />
        <div className="hands">
          {hands.map((cards, h) => {
            const bet = state.hands[h]?.bet ?? 0;
            const t = handTotal(cards).total;
            const active = state.phase === 'player' && h === state.active && state.hands.length > 1;
            return (
              <div key={h} className={`hand${active ? ' active' : ''}`}>
                <div className="cards">
                  {cards.map((c, i) => <PlayingCard key={i} card={c} ring={ringSwapped && i === cards.length - 1 && cards.length === (state.hands[h]?.cards.length ?? 0)} />)}
                </div>
                <div className="hand-line">
                  <span className="hand-total">{hands.length > 1 ? `Hand ${h + 1} · ` : 'You · '}{t > 21 ? `bust (${t})` : t}</span>
                  <span className="hand-bet">{money(bet)}</span>
                </div>
              </div>
            );
          })}
        </div>
        <StatusLine
          snapshot={snapshot}
          result={finished ? state.results.map((r, i) => <div key={i}>{withCommas(r)}</div>) : undefined}
          tone={state.net > 0 ? 'win' : state.net < 0 ? 'loss' : ''}
        />
      </Stage>
      <Controls snapshot={snapshot} actions={actions} onAct={() => setBurst(null)} />
    </>
  );
}
